import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  Trash2,
  AlertCircle,
  FileDiff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  addChangeOrder,
  setChangeOrderStatus,
  removeChangeOrder,
  formatUsd,
} from '@/lib/jobsData';
import { fetchCoLaborTotals } from '@/lib/timeEntriesData';
import { toast } from 'sonner';

/* T&M labor accrued against a change order, vs its optional NTE cap. */
const CoAccrual = ({ labor, nte }) => {
  const hours = Number(labor?.hours) || 0;
  const cost = Number(labor?.cost) || 0;
  const hasCap = nte !== null && nte !== undefined && nte !== '' && Number(nte) > 0;
  const pct = hasCap ? Math.min(100, Math.round((cost / Number(nte)) * 100)) : 0;
  const over = hasCap && cost > Number(nte);
  return (
    <div className="mt-2 rounded-md border border-[hsl(var(--info))]/20 bg-[hsl(var(--info))]/[0.06] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground uppercase tracking-[0.14em] text-[10px]">
          Labor logged
        </span>
        <span className="font-semibold text-foreground tabular-nums">
          {hours.toFixed(2)} hrs · {formatUsd(cost)}
        </span>
      </div>
      {hasCap ? (
        <div className="mt-1.5">
          <div className="h-1.5 w-full rounded-full bg-[hsl(var(--muted))] overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                over ? 'bg-[hsl(var(--destructive))]' : 'bg-[hsl(var(--info))]'
              )}
              style={{ width: `${Math.max(4, pct)}%` }}
            />
          </div>
          <div
            className={cn(
              'mt-1 text-[10px] tabular-nums',
              over ? 'text-[hsl(var(--destructive))]' : 'text-muted-foreground'
            )}
          >
            {over
              ? `Over cap by ${formatUsd(cost - Number(nte))}`
              : `${pct}% of ${formatUsd(nte)} cap`}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const CO_BADGE = {
  pending:
    'bg-[hsl(var(--warning))]/12 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  approved:
    'bg-[hsl(var(--success))]/12 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  denied: 'bg-[hsl(var(--muted))] text-muted-foreground border-border line-through',
};
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', denied: 'Denied' };

const Stat = ({ label, value, tone }) => (
  <div className="rounded-md border border-border bg-[hsl(var(--background))]/40 p-3">
    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </div>
    <div
      className={cn(
        'mt-1 text-lg font-semibold',
        tone === 'success'
          ? 'text-[hsl(var(--success))]'
          : tone === 'warning'
          ? 'text-[hsl(var(--warning))]'
          : 'text-foreground'
      )}
    >
      {value}
    </div>
  </div>
);

export const JobChangeOrdersTab = ({ job, onUpdated }) => {
  // Memoized so the fallback [] is stable and the memos below only recompute
  // when the job's change orders actually change.
  const rawChangeOrders = job?.change_orders;
  const changeOrders = useMemo(
    () => (Array.isArray(rawChangeOrders) ? rawChangeOrders : []),
    [rawChangeOrders]
  );

  const [desc, setDesc] = useState('');
  const [type, setType] = useState('fixed'); // 'fixed' | 'tm'
  const [amount, setAmount] = useState('');
  const [nte, setNte] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyCo, setBusyCo] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [coLabor, setCoLabor] = useState({});

  // Load T&M labor accrual for this job's change orders.
  const tmCoIds = useMemo(
    () =>
      changeOrders
        .filter((c) => (c?.type || 'fixed') === 'tm' && c?.id)
        .map((c) => c.id),
    [changeOrders]
  );
  const tmKey = tmCoIds.join(',');
  useEffect(() => {
    let alive = true;
    if (tmCoIds.length === 0) {
      setCoLabor({});
      return;
    }
    fetchCoLaborTotals(tmCoIds)
      .then((totals) => {
        if (alive) setCoLabor(totals || {});
      })
      .catch(() => {
        /* soft-fail: accrual just won't show */
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tmKey]);

  const summary = useMemo(() => {
    let pending = 0;
    let approvedTotal = 0;
    for (const co of changeOrders) {
      if (co?.status === 'approved') approvedTotal += Number(co.amount) || 0;
      else if (co?.status !== 'denied') pending += 1;
    }
    return { pending, approvedTotal, count: changeOrders.length };
  }, [changeOrders]);

  const resetForm = () => {
    setDesc('');
    setType('fixed');
    setAmount('');
    setNte('');
    setNotes('');
    setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!desc.trim()) {
      setError('Description is required.');
      return;
    }
    // Fixed CO needs a real amount; T&M amount is an optional estimate.
    if (type === 'fixed' && (Number.isNaN(Number(amount)) || Number(amount) === 0)) {
      setError('Enter an amount for a fixed-price change order.');
      return;
    }
    setSubmitting(true);
    try {
      const updated = await addChangeOrder(job, {
        desc,
        type,
        amount: Number(amount) || 0,
        nte: type === 'tm' ? nte : null,
        notes,
      });
      toast.success('Change order added');
      onUpdated?.(updated);
      resetForm();
    } catch (err) {
      setError(err?.message || 'Failed to add change order.');
    } finally {
      setSubmitting(false);
    }
  };

  const decide = async (coId, status) => {
    setBusyCo(coId);
    try {
      const updated = await setChangeOrderStatus(job, coId, status);
      toast.success(
        status === 'approved' ? 'Change order approved' : 'Change order denied'
      );
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.message || 'Failed to update change order.');
    } finally {
      setBusyCo(null);
    }
  };

  const doRemove = async (coId) => {
    setBusyCo(coId);
    try {
      const updated = await removeChangeOrder(job, coId);
      toast.success('Change order removed');
      setConfirmDelete(null);
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.message || 'Failed to remove change order.');
    } finally {
      setBusyCo(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="job-change-orders-tab">
      {/* Summary */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4 grid grid-cols-3 gap-3">
          <Stat label="Total" value={summary.count} />
          <Stat
            label="Pending"
            value={summary.pending}
            tone={summary.pending ? 'warning' : undefined}
          />
          <Stat
            label="Approved → contract"
            value={formatUsd(summary.approvedTotal)}
            tone="success"
          />
        </CardContent>
      </Card>

      {/* List + add */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <FileDiff size={14} className="text-[hsl(var(--primary))]" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Change Orders / Add-Ons
            </div>
          </div>

          {changeOrders.length === 0 ? (
            <div className="mt-3 text-sm text-muted-foreground">
              No change orders yet. Add one below.
            </div>
          ) : (
            <div className="mt-3 space-y-2" data-testid="change-orders-list">
              {changeOrders.map((co) => {
                const isTM = co.type === 'tm';
                const pending = co.status !== 'approved' && co.status !== 'denied';
                return (
                  <div
                    key={co.id}
                    className="rounded-md border border-border bg-[hsl(var(--background))]/40 px-3 py-2.5"
                    data-testid={`change-order-${co.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-foreground">
                            {co.desc || 'Change order'}
                          </span>
                          <Badge
                            variant="outline"
                            className={cn(
                              'uppercase text-[10px] tracking-[0.16em]',
                              isTM
                                ? 'bg-[hsl(var(--info))]/12 text-[hsl(var(--info))] border-[hsl(var(--info))]/30'
                                : 'bg-[hsl(var(--muted))] text-muted-foreground border-border'
                            )}
                          >
                            {isTM ? 'T&M' : 'Fixed'}
                          </Badge>
                          <Badge
                            variant="outline"
                            className={cn(
                              'uppercase text-[10px] tracking-[0.16em]',
                              CO_BADGE[co.status] || CO_BADGE.pending
                            )}
                          >
                            {STATUS_LABEL[co.status] || 'Pending'}
                          </Badge>
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {co.created || ''} · {formatUsd(co.amount)}
                          {isTM ? ' est.' : ''}
                          {isTM
                            ? co.nte !== null && co.nte !== undefined
                              ? ` · cap ${formatUsd(co.nte)}`
                              : ' · no cap'
                            : ''}
                        </div>
                        {co.notes ? (
                          <div className="mt-1 text-xs text-foreground/80">
                            {co.notes}
                          </div>
                        ) : null}
                        {isTM ? (
                          <CoAccrual labor={coLabor[co.id]} nte={co.nte} />
                        ) : null}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {pending ? (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              disabled={busyCo === co.id}
                              onClick={() => decide(co.id, 'approved')}
                              className="text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10"
                              data-testid={`co-approve-${co.id}`}
                              aria-label="Approve change order"
                            >
                              <ThumbsUp size={14} />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              disabled={busyCo === co.id}
                              onClick={() => decide(co.id, 'denied')}
                              className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                              data-testid={`co-deny-${co.id}`}
                              aria-label="Deny change order"
                            >
                              <ThumbsDown size={14} />
                            </Button>
                          </>
                        ) : null}
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={busyCo === co.id}
                          onClick={() => setConfirmDelete(co.id)}
                          className="text-muted-foreground hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                          data-testid={`co-remove-${co.id}`}
                          aria-label="Remove change order"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add form */}
          <form
            onSubmit={submit}
            className="mt-4 space-y-3 border-t border-border/60 pt-4"
            data-testid="co-form"
          >
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              New change order
            </Label>
            <Textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="What's changing or being added…"
              rows={2}
              className="bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 resize-none"
              data-testid="co-desc"
            />

            {/* Type toggle */}
            <div className="grid grid-cols-2 gap-2">
              {[
                ['fixed', 'Fixed price'],
                ['tm', 'Time & Material'],
              ].map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setType(val)}
                  className={cn(
                    'rounded-md border px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] transition-colors',
                    type === val
                      ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                      : 'border-border text-muted-foreground hover:bg-[hsl(var(--muted))]'
                  )}
                  data-testid={`co-type-${val}`}
                >
                  {lbl}
                </button>
              ))}
            </div>

            <div className={cn('grid gap-2', type === 'tm' ? 'sm:grid-cols-2' : 'grid-cols-1')}>
              <Input
                type="number"
                inputMode="decimal"
                step="100"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={type === 'tm' ? 'Estimated $ (optional)' : 'Amount $'}
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                data-testid="co-amount"
              />
              {type === 'tm' ? (
                <Input
                  type="number"
                  inputMode="decimal"
                  step="100"
                  value={nte}
                  onChange={(e) => setNte(e.target.value)}
                  placeholder="Not-to-exceed cap (optional)"
                  className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                  data-testid="co-nte"
                />
              ) : null}
            </div>

            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optional)"
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
              data-testid="co-notes"
            />

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                data-testid="co-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving
                  </>
                ) : (
                  <>
                    <Plus size={14} className="mr-1.5" />
                    Add change order
                  </>
                )}
              </Button>
            </div>

            {error ? (
              <Alert
                className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
                data-testid="co-error"
              >
                <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </CardContent>
      </Card>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-[hsl(var(--card))] border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              Remove change order?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This permanently removes it from the job.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-foreground hover:bg-[hsl(var(--muted))]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => doRemove(confirmDelete)}
              className="bg-[hsl(var(--destructive))] text-destructive-foreground hover:bg-[hsl(var(--destructive))]/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JobChangeOrdersTab;
